import { useEffect, useMemo, useRef } from 'react'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'
import type { DriveListSort } from '@/user_files/sorting'
import { kExternalStorageProviderDisplayName } from '@/constants/constants-external-storage'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { usePollWhileVisible } from '@/hooks/usePollWhileVisible'
import { kProcessingPollIntervalMs } from '@/uppy-file-upload/constants'
import type { BrowseMatterFilesProps } from '@/user_files/components/BrowseMatterFiles'
import { useBrowseFiles } from '@/user_files/components/useBrowseFiles'
import { useMatterExternalSyncStatus } from '@/user_files/external-storage/hooks/useMatterExternalSyncStatus'
import { PROVIDER_COMPACT_DISPLAY_NAME } from '@/user_files/external-storage/ProviderMeta'
import { filterVisibleExternalStorageItems, isVisibleExternalStorageItem } from '@/user_files/external-storage/utils/external-storage-items'
import { groupExtractedFiles } from '@/user_files/utils/group-extracted-files'

type UseExternalFilesTabParams = {
  active: boolean
  fallbackBreadcrumbItems: BreadcrumbItemType[]
  matterId: string
  open: boolean
  /**
   * Folder to open the synced subtree on instead of its root — set when a search result sends the
   * user straight to a synced folder. Browsing resets to the root, so this has to be the browse
   * hook's initial folder rather than a navigation applied after it opens.
   */
  initialFolderIdOverride?: string | null
  /** Row order to request for the synced listing; shared with the matter view. */
  sort?: DriveListSort
}

/**
 * Whether a synced DMS is available to show as a source, distinguishing "we checked and there
 * isn't one" (unavailable) from "we couldn't tell" (error).
 */
export type DmsDiscoveryStatus = 'loading' | 'available' | 'unavailable' | 'error'

type ExternalFilesBrowseProps = Pick<
  BrowseMatterFilesProps,
  'blockingError' | 'breadcrumbItems' | 'files' | 'folders' | 'initialSyncSourceLabel' | 'loading' | 'onNavigateToFolder' | 'onRetry'
>

export function useExternalFilesTab({ active, fallbackBreadcrumbItems, matterId, open, initialFolderIdOverride, sort }: UseExternalFilesTabParams) {
  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const externalSyncStatus = useMatterExternalSyncStatus({
    matterId,
    enabled: open && driveSyncEnabled,
  })
  const { reloadMapping } = externalSyncStatus

  const activeExternalMapping = externalSyncStatus.activeMapping
  const syncedProvider = externalSyncStatus.syncedProvider
  const syncedProviderLabel = syncedProvider ? kExternalStorageProviderDisplayName[syncedProvider] : null
  const showExternalTab = driveSyncEnabled && !!activeExternalMapping && !!syncedProvider && !!syncedProviderLabel
  const dmsDiscoveryStatus: DmsDiscoveryStatus = externalSyncStatus.error
    ? 'error'
    : externalSyncStatus.loading
    ? 'loading'
    : showExternalTab
    ? 'available'
    : 'unavailable'

  const initialSnapshotPending = activeExternalMapping != null && activeExternalMapping.last_synced_at == null
  const initialSyncPending = Boolean(initialSnapshotPending || activeExternalMapping?.has_pending_files)
  const initialSyncInProgress = Boolean(initialSyncPending && !activeExternalMapping?.failure_message)
  const hasUnhealthyAccount = activeExternalMapping?.account_status === 'error' || activeExternalMapping?.account_status === 'reauth_required'

  // Reconcile cached mapping and account-health state whenever this surface opens. The thunk
  // de-duplicates this with the hook's first discovery request when no cached request exists.
  useEffect(() => {
    if (!open || !driveSyncEnabled || !matterId) return
    reloadMapping()
  }, [driveSyncEnabled, matterId, open, reloadMapping])

  const externalFilesBrowse = useBrowseFiles({
    sort,
    matterId,
    // A reactivated mapping can reuse a Paxton root whose previous listing is still cached.
    // Wait for the authoritative provider snapshot before browsing so the first load reflects
    // restoration and upstream deletion decisions from that snapshot.
    open: open && showExternalTab && active && !!activeExternalMapping && !initialSnapshotPending,
    initialFolderId: initialFolderIdOverride ?? activeExternalMapping?.user_folder_id,
    initialFolderRequired: true,
  })
  const retryExternalFiles = externalFilesBrowse.retry

  useEffect(() => {
    if (!open || !active || !showExternalTab || initialSnapshotPending) return

    const refreshWhenVisible = () => {
      if (!document.hidden) retryExternalFiles()
    }
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => document.removeEventListener('visibilitychange', refreshWhenVisible)
  }, [active, initialSnapshotPending, open, retryExternalFiles, showExternalTab])

  const externalFolders = useMemo(
    () => (initialSnapshotPending ? [] : filterVisibleExternalStorageItems(externalFilesBrowse.folders, syncedProvider)),
    [externalFilesBrowse.folders, initialSnapshotPending, syncedProvider]
  )
  const externalFiles = useMemo(
    () =>
      initialSnapshotPending
        ? []
        : groupExtractedFiles(externalFilesBrowse.files)
            .filter(({ root }) => isVisibleExternalStorageItem(root, syncedProvider))
            .map(({ file }) => file),
    [externalFilesBrowse.files, initialSnapshotPending, syncedProvider]
  )

  // Refresh the mapping while its first provider snapshot or hidden imports remain. Browsing opens
  // after the snapshot so already-visible rows appear while later imports continue in the
  // background; normal provider synchronization remains owned by the backend workflow.
  usePollWhileVisible(
    () => {
      reloadMapping()
    },
    kProcessingPollIntervalMs,
    open && active && showExternalTab && initialSyncPending
  )

  // The mapping response and folder listing are independent requests. When the mapping first says
  // all hidden imports are visible, refetch the list once more so a listing that raced that status
  // transition cannot leave the completed files hidden until the view is reopened.
  const hadPendingInitialSyncRef = useRef(false)
  useEffect(() => {
    if (!open || !active || !showExternalTab) {
      hadPendingInitialSyncRef.current = false
      return
    }
    if (initialSyncPending) {
      hadPendingInitialSyncRef.current = true
      return
    }
    if (!hadPendingInitialSyncRef.current) return

    hadPendingInitialSyncRef.current = false
    retryExternalFiles()
  }, [active, initialSyncPending, open, retryExternalFiles, showExternalTab])

  // useBrowseFiles's breadcrumb is [matter crumb, synced-root-folder crumb, ...deeper folders],
  // with the matter crumb pointing at the matter's own root — right for browsing internal files,
  // wrong here: the synced subtree is rooted at the mapping's folder, not the matter root, so
  // that crumb would navigate outside the synced folder entirely. The synced root folder's own
  // name is redundant with the matter name in this view, so it's dropped; the matter crumb takes
  // over as the trail's anchor, repointed at the synced root (undefined/non-clickable when the
  // synced root is itself the active folder, matching how the last crumb is normally inert).
  const [matterCrumb, , ...deeperCrumbs] = externalFilesBrowse.breadcrumbItems
  const externalBreadcrumbItems: BreadcrumbItemType[] = matterCrumb
    ? [
        {
          id: matterCrumb.id,
          label: matterCrumb.label,
          onClick:
            deeperCrumbs.length > 0 && activeExternalMapping ? () => externalFilesBrowse.onNavigateToFolder(activeExternalMapping.user_folder_id) : undefined,
        },
        ...deeperCrumbs,
      ]
    : []

  const browseProps: ExternalFilesBrowseProps = {
    breadcrumbItems: externalBreadcrumbItems.length > 0 ? externalBreadcrumbItems : fallbackBreadcrumbItems,
    blockingError: externalSyncStatus.error ? 'Unable to load files.' : initialSnapshotPending ? undefined : externalFilesBrowse.error,
    files: activeExternalMapping ? externalFiles : [],
    folders: activeExternalMapping ? externalFolders : [],
    initialSyncSourceLabel: initialSyncInProgress && syncedProvider ? PROVIDER_COMPACT_DISPLAY_NAME[syncedProvider] : undefined,
    loading: externalSyncStatus.loading || (!!activeExternalMapping && externalFilesBrowse.loading && !initialSyncInProgress),
    onNavigateToFolder: externalFilesBrowse.onNavigateToFolder,
    onRetry: externalSyncStatus.error
      ? () => {
          externalSyncStatus.reload()
        }
      : externalFilesBrowse.retry,
  }

  return {
    activeExternalMapping,
    browseProps,
    dmsDiscoveryStatus,
    /** Folder currently being browsed. Identifies the rows on screen for selection state. */
    browsedFolderId: externalFilesBrowse.folderId,
    externalFiles,
    externalFolders,
    externalSyncStatus,
    hasUnhealthyAccount,
    initialSyncInProgress,
    showExternalTab,
    syncedProvider,
    syncedProviderLabel,
  }
}
